import styled, { css } from 'styled-components';

// Re-purposing BookmarkUrl to StyledUrlWrapper or creating a new one.
// For now, let's define new ones for clarity, mirroring ProjectPage.styles.js
// but adapted for the card.

export const StyledUrlWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px; /* Consistent with ProjectHeader */
    color: ${props => props.textColor || (props.hasBackground ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.9)')};
    padding: 8px 0; /* Adjusted padding for card context */
    flex-grow: 1;
    position: relative; /* For EditButton positioning */

    /* Ensure the wrapper itself is visible for hover effects if EditButton relies on it */
    &:hover .edit-button-class { // Assuming EditButton gets a class for targeting
        opacity: 1;
    }
`;

export const ProjectLinkIcon = styled.svg.attrs({
    xmlns: "http://www.w3.org/2000/svg",
    width: "16", // Consistent with ProjectHeader's icon size
    height: "16",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
})`
    flex-shrink: 0;
    opacity: 0.7;
    path:first-child {
        d: path("M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71");
    }
    path:last-child {
        d: path("M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71");
    }
    color: ${props => props.textColor || (props.hasBackground ? '#fff' : '#ccc')}; /* Ensure icon color is visible */
`;


export const StyledProjectUrlInput = styled.input`
    background: transparent;
    border: none;
    font-size: 14px; /* Consistent with ProjectHeader */
    color: ${props => props.textColor || (props.hasBackground ? '#fff' : '#ddd')};
    width: 100%;
    padding: 4px;
    border-radius: 3px;
    box-sizing: border-box;

    &:hover {
        background: ${props => props.readOnly ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
    }

    &:focus {
        outline: none;
        background: rgba(255, 255, 255, 0.15);
    }

    &::placeholder {
        color: ${props => props.hasBackground ? 'rgba(255, 255, 255, 0.7)' : 'rgba(204, 204, 204, 0.7)'};
    }
`;

export const StyledProjectUrlDisplay = styled.a`
    font-size: 14px; /* Consistent with ProjectHeader */
    color: ${props => props.textColor || (props.hasBackground ? '#ADD8E6' : '#ADD8E6')}; /* Light blue, stands out on dark bg */
    text-decoration: underline;
    cursor: pointer;
    padding: 4px;
    border-radius: 3px;
    display: block; /* Changed from flex to allow text overflow */
    width: calc(100% - 20px); /* Adjust width to prevent overlap with edit button */
    word-break: break-all;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;


    &:hover {
        /* background: rgba(255, 255, 255, 0.1); */
        color: ${props => props.hasBackground ? '#87CEEB' : '#87CEEB'}; /* Lighter blue on hover */
    }
`;

export const StyledEditButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 3px;
    color: ${props => props.textColor || '#fff'};
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0; /* Hidden by default */
    transition: opacity 0.2s ease-in-out;
    position: absolute; /* Position it within the wrapper */
    right: 0px; /* Align to the right */
    top: 50%;
    transform: translateY(-50%);

    ${StyledUrlWrapper}:hover & { /* Show on wrapper hover */
        opacity: 1;
    }

    svg {
     width: 14px; /* Smaller icon for card */
     height: 14px;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`;


// Original BookmarkUrl and UrlText might be deprecated or modified if StyledUrlWrapper covers their use cases.
// For now, keeping them in case they are used elsewhere or for other views.
export const BookmarkUrl = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${props => props.hasBackground ? '#fff' : '#fff'};
    opacity: 0.9;
    padding: 16px 0;
    flex-grow: 1;
    margin: auto;

    svg { /* This was for the old UrlIcon */
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        opacity: 0.7;
    }
`;

export const UrlText = styled.span`
    font-size: 18px; /* This was larger than the new input/display style */
    font-weight: 500;
    line-height: 1.4;
    word-break: break-all;
`;

export const Card = styled.div`
  background: ${props => props.background ? `url(${props.background})` : props.backgroundColor || 'rgb(40, 46, 51)'};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  padding: 20px;
  padding-top: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  /* overflow: hidden; */ /* Commented out to allow edit button to potentially overflow if not perfectly contained */
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 200px;
  max-width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.background ?
        'linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7))' :
        'none'};
    pointer-events: none;
    border-radius: 8px; /* Ensure gradient respects border radius */
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #0079bf;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::after {
    opacity: 1;
  }

  ${props => props.viewType === 'BOOKMARK' && `
        /* justify-content: center; */ /* May not be needed with new flex layout */
        min-height: 160px; /* Retain min-height for bookmark view */
        
        /* ProjectMeta is already handled by its own rendering logic in ProjectCard.js */
        /* ${ProjectMeta} {
            display: none;
        } */
    `}
`;

export const StarButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.isStarred ? '#f2d600' : '#6b778c'};
  opacity: ${props => props.isStarred ? 1 : 0};
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  ${Card}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(9, 30, 66, 0.08);
    transform: scale(1.1);
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }

  @media (max-width: 768px) {
      opacity: 1;
    }
`;

export const ProjectTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.textColor || (props.hasBackground ? '#fff' : '#fff')};
  line-height: 1.4;
  padding-right: 32px; /* Space for icons */
  position: relative;
  z-index: 1; /* Ensure title is above ::before pseudo-element */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden; /* Added for text-overflow to work */
  margin-bottom: 4px;
`;

export const ProjectTagline = styled.p`
  color: ${props => props.textColor ? props.textColor : (props.hasBackground ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  flex-grow: 1;
  position: relative;
  z-index: 1;
  overflow: hidden; /* Ensure tagline content does not break layout */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

export const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 12px;
  border-top: 1px solid ${props => props.hasBackground ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.2)'}; /* Consistent border color */
  color: ${props => props.textColor || (props.hasBackground ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 13px;
  position: relative;
  z-index: 1;
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.textColor || (props.hasBackground ? 'rgba(255, 255, 255, 0.8)' : 'inherit')};
`;

export const MetaIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px; /* Or specific size for icons */
`;

export const LastUpdated = styled.div`
  font-size: 12px;
  color: ${props => props.textColor || (props.hasBackground ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.7)')};
  margin-left: auto;
  position: relative;
  z-index: 1;
`;

export const DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s ease;
  background: linear-gradient(
    to bottom,
    rgba(9, 30, 66, 0.13),
    transparent
  );
  z-index: 2;

  ${Card}:hover & {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    opacity: 1;
  }
`;

export const MenuButton = styled.button`
    position: absolute;
    top: 12px;
    right: 48px; /* Adjusted to make space for ViewButton */
    background: transparent;
    border: none;
    color: #B6C2CF;
    border-radius: 3px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 2;

    @media (max-width: 768px) {
      opacity: 1;
    }

    ${Card}:hover & {
        opacity: 1;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

export const MenuDropdown = styled.div`
    position: absolute;
    top: 12px; /* Align with MenuButton */
    right: 74px; /* Position relative to MenuButton */
    margin-top: 4px; /* Small gap from button */
    background: #282E33;
    border-radius: 3px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    min-width: 180px;
    z-index: 1000;
`;

export const MenuItem = styled.button`
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: #B6C2CF;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: #3B444C;
    }

    svg {
        flex-shrink: 0;
    }
`;

export const ViewButton = styled.button`
  position: absolute;
  top: 12px;
  right: 84px; /* Positioned to the left of StarButton */
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${props => props.isActive ? '#0079bf' : '#6b778c'};
  opacity: ${props => props.isActive ? 1 : 0};
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  ${Card}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(9, 30, 66, 0.08);
    transform: scale(1.1);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
      opacity: 1;
    }
`;

export const TodoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  position: relative;
  z-index: 1; /* Ensure content is above ::before */
`;

export const TodoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.textColor || (props.hasBackground ?
    `rgba(255, 255, 255, ${props.completed ? 0.6 : 0.8})` :
    `rgba(255, 255, 255, ${props.completed ? 0.6 : 0.8})`)};
  font-size: 14px;
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
`;

export const TodoCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.textColor || 'rgba(255, 255, 255, 0.6)'};
  border-radius: 3px;
  position: relative;
  flex-shrink: 0;

  ${props => props.checked && `
    background: rgba(255, 255, 255, 0.6);
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #282E33;
      font-size: 12px;
    }
  `}
`;

export const TodoText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// UrlIcon is now ProjectLinkIcon - this can be removed if not used elsewhere
// export const UrlIcon = styled.svg.attrs({
//   viewBox: '0 0 24 24',
//   fill: 'none',
//   stroke: 'currentColor',
//   strokeWidth: '2',
//   strokeLinecap: 'round',
//   strokeLinejoin: 'round'
// })`
//     width: 16px;
//     height: 16px;
//     flex-shrink: 0;

//     path {
//         d: path("M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71");
//     }
//     path:last-child {
//         d: path("M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71");
//     }
// `;
